import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

export type ActivityPhase =
  | 'IDLE'
  | 'PROCESSING'
  | 'CORE'
  | 'PROPOSAL'
  | 'SIMULATION'
  | 'VALIDATION'
  | 'AUTHORIZATION'
  | 'BUILD'
  | 'TEST'
  | 'DEPLOY'
  | 'EVIDENCE'
  | 'ROLLBACK'
  | 'COMPLETE'
  | 'ERROR'

export interface LAIAActivity {
  phase:
    ActivityPhase

  label:
    string

  detail?:
    string

  startedAt:
    number | null

  step?:
    number

  totalSteps?:
    number

  progress?:
    number
}

interface ActivityValue {
  activity:
    LAIAActivity

  begin:
    (
      phase: ActivityPhase,
      label: string,
      extra?: Partial<LAIAActivity>,
    ) => void

  update:
    (
      changes:
        Partial<LAIAActivity>,
    ) => void

  complete:
    (
      detail?: string,
    ) => void

  fail:
    (
      detail?: string,
    ) => void

  reset:
    () => void
}

const IDLE:
LAIAActivity = {
  phase:
    'IDLE',

  label:
    'LAIA disponible',

  startedAt:
    null,
}

const Context =
  createContext<
    ActivityValue | null
  >(null)

export function ActivityProvider({
  children,
}: {
  children:
    ReactNode
}) {
  const [
    activity,
    setActivity,
  ] =
    useState<LAIAActivity>(
      IDLE,
    )

  const value =
    useMemo<ActivityValue>(
      () => ({
        activity,

        begin(
          phase,
          label,
          extra = {},
        ) {
          setActivity({
            phase,
            label,
            startedAt:
              Date.now(),
            ...extra,
          })
        },

        update(changes) {
          setActivity(
            current => ({
              ...current,
              ...changes,
            }),
          )
        },

        complete(detail) {
          setActivity(
            current => ({
              ...current,

              phase:
                'COMPLETE',

              label:
                'Operación completada',

              detail,
            }),
          )
        },

        fail(detail) {
          setActivity(
            current => ({
              ...current,

              phase:
                'ERROR',

              label:
                'Requiere atención',

              detail,
            }),
          )
        },

        reset() {
          setActivity(
            IDLE,
          )
        },
      }),
      [activity],
    )

  return (
    <Context.Provider
      value={value}
    >
      {children}
    </Context.Provider>
  )
}

export function useLAIAActivity() {
  const value =
    useContext(Context)

  if (!value) {
    throw new Error(
      'ActivityProvider missing',
    )
  }

  return value
}
